import { useState } from 'react'
import {
  TIERS, getTierByCi, getNextTier, getCiToNext,
  getCiProgress, THEME_STYLES
} from '../lib/press'
import ProfileCard from './ProfileCard'

// ── TierCard ─────────────────────────────────────────────────
function TierCard({ tier, active, unlocked }) {
  const theme = THEME_STYLES[tier.theme] || {}
  return (
    <div style={{
      border: `1px solid ${unlocked ? tier.color : 'var(--rule)'}`,
      borderLeft: `3px solid ${unlocked ? tier.color : 'var(--rule)'}`,
      padding: '0.75rem 1rem',
      background: unlocked && active ? 'rgba(200,64,26,0.04)' : 'var(--paper)',
      opacity: unlocked ? 1 : 0.4,
      transition: 'all 0.2s',
      position: 'relative',
    }}>
      {active && (
        <div style={{
          position: 'absolute',
          top: 6, right: 8,
          fontFamily: "'Courier New', monospace",
          fontSize: '0.5rem',
          letterSpacing: '0.14em',
          color: 'var(--accent)',
          textTransform: 'uppercase',
        }}>
          CURRENT
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.2rem' }}>
        <span style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '1.1rem',
          color: unlocked ? tier.color : 'var(--rule)',
          lineHeight: 1,
        }}>
          {tier.badge}
        </span>
        <div>
          <div style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.6rem',
            letterSpacing: '0.1em',
            color: unlocked ? tier.color : 'var(--muted)',
            textTransform: 'uppercase',
          }}>
            {tier.indicator}
          </div>
          <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            color: unlocked ? 'var(--ink)' : 'var(--muted)',
          }}>
            {tier.title}
          </div>
        </div>
      </div>
      <div style={{
        fontFamily: "'Courier New', monospace",
        fontSize: '0.58rem',
        color: 'var(--muted)',
        lineHeight: 1.5,
      }}>
        {tier.ci === Infinity ? 'Editor-appointed' : `${tier.ci} CI`}
      </div>
    </div>
  )
}

// ── ThemePreview ──────────────────────────────────────────────
function ThemePreview({ tier, active, onActivate, columnInches, isVoice }) {
  return (
    <div style={{ cursor: 'pointer', outline: active ? `3px solid ${tier.color}` : '3px solid transparent', outlineOffset: 3, transition: 'outline 0.2s' }} onClick={onActivate}>
      <ProfileCard
        displayName="Your Name"
        columnInches={columnInches}
        isVoice={isVoice}
        pressTheme={tier.theme}
        size="dashboard"
      />
      <div style={{
        fontFamily: "'Courier New', monospace",
        fontSize: '0.58rem',
        color: active ? tier.color : 'var(--muted)',
        textAlign: 'center',
        marginTop: '-1rem',
        marginBottom: '0.5rem',
        letterSpacing: '0.08em',
      }}>
        {active ? '✓ ACTIVE' : tier.title}
      </div>
    </div>
  )
}

// ── Main Pressroom Component ──────────────────────────────────
export default function Pressroom({ columnInches = 0, isVoice = false, activeTheme, onThemeChange }) {
  const [view, setView] = useState('progress') // 'progress' | 'themes' | 'badges'
  const currentTier = getTierByCi(columnInches, isVoice)
  const nextTier = getNextTier(currentTier.rank)
  const progress = getCiProgress(columnInches, currentTier.rank)
  const ciToNext = getCiToNext(columnInches, currentTier.rank)

  const unlockedTiers = TIERS.filter(t =>
    t.ci !== Infinity && columnInches >= t.ci
  )
  const lockedTiers = TIERS.filter(t =>
    t.ci !== Infinity && columnInches < t.ci
  )

  return (
    <div>
      <style>{`
        @keyframes press-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(200,122,58,0.4); }
          50%       { box-shadow: 0 0 0 6px rgba(200,122,58,0); }
        }
        @keyframes press-ribbon {
          0%   { border-color: #C87A7A; }
          33%  { border-color: #7A9CB8; }
          66%  { border-color: #C8401A; }
          100% { border-color: #C87A7A; }
        }
        @keyframes press-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.3); }
          50%       { box-shadow: 0 0 12px 2px rgba(212,175,55,0.2); }
        }
        @keyframes press-crimson {
          0%, 100% { box-shadow: 0 0 0 0 rgba(200,64,26,0.4); }
          50%       { box-shadow: 0 0 16px 4px rgba(200,64,26,0.15); }
        }
        @keyframes badge-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header — current rank card */}
      <div style={{
        border: `1px solid ${currentTier.color}`,
        borderLeft: `4px solid ${currentTier.color}`,
        padding: '1.5rem',
        marginBottom: '1.5rem',
        background: 'var(--cream)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Watermark */}
        <div style={{
          position: 'absolute',
          right: -10, bottom: -20,
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '5rem',
          fontWeight: 700,
          color: 'rgba(0,0,0,0.03)',
          userSelect: 'none',
          lineHeight: 1,
        }}>
          {currentTier.badge}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div>
            <div style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '0.6rem',
              letterSpacing: '0.18em',
              color: currentTier.color,
              textTransform: 'uppercase',
              marginBottom: '0.3rem',
            }}>
              {currentTier.indicator} — The Press
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '1.8rem',
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: '0.3rem',
              letterSpacing: '-0.02em',
            }}>
              {currentTier.title}
            </h2>
            <p style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '0.68rem',
              color: 'var(--muted)',
              letterSpacing: '0.02em',
            }}>
              {currentTier.description}
            </p>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '2.5rem',
              fontWeight: 700,
              color: currentTier.color,
              lineHeight: 1,
            }}>
              {columnInches}
            </div>
            <div style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '0.58rem',
              color: 'var(--muted)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              Column Inches
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {nextTier && (
          <div style={{ marginTop: '1.25rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.4rem',
            }}>
              <span style={{
                fontFamily: "'Courier New', monospace",
                fontSize: '0.58rem',
                color: 'var(--muted)',
                letterSpacing: '0.06em',
              }}>
                {progress}% to {nextTier.title}
              </span>
              <span style={{
                fontFamily: "'Courier New', monospace",
                fontSize: '0.58rem',
                color: currentTier.color,
                letterSpacing: '0.06em',
              }}>
                {ciToNext} CI needed
              </span>
            </div>
            {/* Typeset column bar */}
            <div style={{
              height: 8,
              background: 'var(--rule)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Column lines inside bar */}
              {Array(20).fill(null).map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  left: `${i * 5}%`,
                  top: 0, bottom: 0,
                  width: 1,
                  background: 'rgba(255,255,255,0.15)',
                  zIndex: 1,
                }} />
              ))}
              <div style={{
                height: '100%',
                width: `${progress}%`,
                background: currentTier.color,
                transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)',
              }} />
            </div>
          </div>
        )}

        {currentTier.rank === 11 && !isVoice && (
          <p style={{
            marginTop: '1rem',
            fontFamily: "'Courier New', monospace",
            fontSize: '0.65rem',
            color: '#D4AF37',
            letterSpacing: '0.06em',
            fontStyle: 'italic',
          }}>
            The Voice is by editor appointment only. Keep writing.
          </p>
        )}
      </div>

      {/* Sub-nav */}
      <div style={{
        display: 'flex',
        gap: 0,
        borderBottom: '1px solid var(--rule)',
        marginBottom: '1.5rem',
      }}>
        {[
          ['progress', 'Track Progress'],
          ['themes', 'Profile Themes'],
          ['badges', 'All Ranks'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${view === key ? 'var(--ink)' : 'transparent'}`,
              padding: '0.65rem 1.1rem',
              fontFamily: "'Courier New', monospace",
              fontSize: '0.62rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              color: view === key ? 'var(--ink)' : 'var(--muted)',
              transition: 'all 0.15s',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Progress view — earning guide */}
      {view === 'progress' && (
        <div>
          <h3 style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.62rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            marginBottom: '1rem',
          }}>
            How to Earn Column Inches
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            background: 'var(--rule)',
            border: '1px solid var(--rule)',
            marginBottom: '2rem',
          }}>
            {[
              { action: 'Publish a Culture piece (under 600 words)', ci: '+8 CI' },
              { action: 'Publish a Culture piece (600–1200 words)', ci: '+15 CI' },
              { action: 'Publish a Culture piece (1200+ words)', ci: '+22 CI' },
              { action: 'Publish a Journal piece', ci: '+35 CI' },
              { action: 'Receive a "Brought Me Here" reaction', ci: '+5 CI' },
              { action: 'Receive a "Shifts Something" reaction', ci: '+4 CI' },
              { action: 'Receive a "Sits With Me" reaction', ci: '+3 CI' },
              { action: 'Receive any other reaction', ci: '+2 CI' },
              { action: 'Receive a comment on your piece', ci: '+3 CI' },
              { action: 'Leave a comment that gets replied to', ci: '+2 CI' },
              { action: 'First publish of the month', ci: '+5 CI bonus' },
              { action: '2 months published in a row', ci: '+10 CI streak' },
              { action: '3+ months published in a row', ci: '+20 CI streak' },
              { action: 'Piece selected for Piece of the Month', ci: '+25 CI bonus' },
            ].map((row, i) => (
              <div key={i} style={{
                background: 'var(--paper)',
                padding: '0.65rem 1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
              }}>
                <span style={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: '0.68rem',
                  color: 'var(--muted)',
                }}>
                  {row.action}
                </span>
                <span style={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: '0.68rem',
                  color: 'var(--accent)',
                  fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {row.ci}
                </span>
              </div>
            ))}
          </div>

          <h3 style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.62rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            marginBottom: '1rem',
          }}>
            Rank Thresholds
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 1,
            background: 'var(--rule)',
            border: '1px solid var(--rule)',
          }}>
            {TIERS.map(tier => (
              <TierCard
                key={tier.rank}
                tier={tier}
                active={tier.rank === currentTier.rank}
                unlocked={tier.ci === Infinity ? isVoice : columnInches >= tier.ci}
              />
            ))}
          </div>
        </div>
      )}

      {/* Themes view */}
      {view === 'themes' && (
        <div>
          <p style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.68rem',
            color: 'var(--muted)',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          }}>
            Unlock profile themes as you rank up. Click to activate one you've unlocked.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '1rem',
          }}>
            {TIERS.filter(t => t.ci !== Infinity || isVoice).map(tier => {
              const unlocked = tier.ci === Infinity ? isVoice : columnInches >= tier.ci
              return (
                <div key={tier.rank} style={{ opacity: unlocked ? 1 : 0.35 }}>
                  <ThemePreview
                    tier={tier}
                    active={activeTheme === tier.theme}
                    onActivate={() => unlocked && onThemeChange?.(tier.theme)}
                    columnInches={columnInches}
                    isVoice={isVoice}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Badges view */}
      {view === 'badges' && (
        <div>
          <p style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.68rem',
            color: 'var(--muted)',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          }}>
            Your badge appears next to your name on all posts and comments.
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            background: 'var(--rule)',
            border: '1px solid var(--rule)',
          }}>
            {TIERS.map(tier => {
              const unlocked = tier.ci === Infinity ? isVoice : columnInches >= tier.ci
              const active = tier.rank === currentTier.rank
              return (
                <div key={tier.rank} style={{
                  background: active ? 'rgba(200,64,26,0.04)' : 'var(--paper)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.25rem',
                  opacity: unlocked ? 1 : 0.4,
                }}>
                  <span style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '1.5rem',
                    color: unlocked ? tier.color : 'var(--rule)',
                    width: 32,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}>
                    {tier.badge}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.15rem' }}>
                      <span style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: unlocked ? 'var(--ink)' : 'var(--muted)',
                      }}>
                        {tier.title}
                      </span>
                      <span style={{
                        fontFamily: "'Courier New', monospace",
                        fontSize: '0.58rem',
                        color: tier.color,
                        letterSpacing: '0.08em',
                      }}>
                        {tier.indicator}
                      </span>
                      {active && (
                        <span style={{
                          background: tier.color,
                          color: '#fff',
                          fontFamily: "'Courier New', monospace",
                          fontSize: '0.48rem',
                          letterSpacing: '0.1em',
                          padding: '1px 5px',
                          textTransform: 'uppercase',
                        }}>
                          YOU
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontFamily: "'Courier New', monospace",
                      fontSize: '0.6rem',
                      color: 'var(--muted)',
                    }}>
                      {tier.description}
                    </div>
                  </div>
                  <div style={{
                    fontFamily: "'Courier New', monospace",
                    fontSize: '0.6rem',
                    color: 'var(--muted)',
                    flexShrink: 0,
                    textAlign: 'right',
                  }}>
                    {tier.ci === Infinity ? 'By appointment' : `${tier.ci} CI`}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
